import type { DrawingToolInstance } from "features/drawing-tool/model";
import type {
  IndicatorInstance,
  IndicatorName,
} from "features/indicators/model";

/**
 * Чистые хелперы, вынесенные из websocket.ts: логику пары/settings можно
 * покрыть unit-тестами без мока всего WebSocket singleton.
 */

export const isSettingsSaveAck = (
  settings: Record<string, unknown> | null,
): boolean => !!settings && "status" in settings;

export type AccountSettingsState = {
  accountPreferenceSaved: boolean;
};

export type NonPairUserSettings = {
  settingsPatch: {
    demo_balance?: boolean;
    real_balance?: boolean;
    sound_enabled: boolean;
    notifications_enabled: boolean;
    accountPreferenceSaved: boolean;
  } | null;
  drawingInstances: DrawingToolInstance[] | null;
  indicatorInstances: IndicatorInstance[] | null;
  indicatorFavorites: IndicatorName[] | null;
};

/** demo/real, sound, indicators, drawing — symbol/pair из global settings намеренно игнорируем. */
export const extractNonPairSettings = (
  settings: Record<string, unknown>,
  currentSettings: AccountSettingsState,
): NonPairUserSettings => {
  const hasExplicitPreference = !!(
    settings.demo_balance || settings.real_balance
  );
  const alreadySetByBalance =
    currentSettings.accountPreferenceSaved && !hasExplicitPreference;

  return {
    settingsPatch: {
      ...(alreadySetByBalance
        ? {}
        : {
            demo_balance: (settings.demo_balance as boolean) || false,
            real_balance: (settings.real_balance as boolean) || false,
          }),
      sound_enabled: settings.sound_enabled !== false,
      notifications_enabled: settings.notifications_enabled !== false,
      accountPreferenceSaved: alreadySetByBalance || hasExplicitPreference,
    },
    drawingInstances: Array.isArray(settings.drawing_instances)
      ? (settings.drawing_instances as DrawingToolInstance[])
      : null,
    indicatorInstances: Array.isArray(settings.indicator_instances)
      ? (settings.indicator_instances as IndicatorInstance[])
      : null,
    indicatorFavorites: Array.isArray(settings.indicator_favorites)
      ? (settings.indicator_favorites as IndicatorName[])
      : null,
  };
};

/** Не слать повторный t=9, если подписка WS уже на запрошенной паре. */
export const shouldSendCurrencySelection = (
  currentSymbol: string | null,
  currencyPair: string,
): boolean => currentSymbol !== currencyPair;

/** symbol/pair из GET settings — только для одноразового cold start на t=16. */
export const extractSavedPairSymbol = (
  settings: Record<string, unknown> | null | undefined,
): string | null => {
  if (!settings) return null;
  const raw = settings.symbol ?? settings.pair;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
};
